import { config } from './config';
import type { Channel, Chapter, Me, Video } from './types';
import { MOCK_CHANNELS, MOCK_VIDEOS, MOCK_CHAPTERS, MOCK_ME } from './mock';

let tokenGetter: () => string | undefined = () => undefined;

export function setTokenGetter(fn: () => string | undefined): void {
  tokenGetter = fn;
}

// VITE_API_BASE_URL が未設定のときだけ、組み込みモックで画面を確認できる。
// 設定されていれば（本物 AWS でもローカル LocalStack でも）その API を叩く。
const useMock = !import.meta.env.VITE_API_BASE_URL;

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const token = tokenGetter();
  const res = await fetch(`${config.apiBaseUrl}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) throw new Error(`API ${res.status}: ${await res.text()}`);
  return res.json() as Promise<T>;
}

const withDuration = (v: Video): Video => ({
  ...v,
  duration: fmt(v.durationSec),
});

const fmt = (s: number): string => {
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
};

export const api = {
  async listVideos(channelId?: string): Promise<Video[]> {
    if (useMock) {
      return (channelId ? MOCK_VIDEOS.filter((v) => v.channelId === channelId) : MOCK_VIDEOS).map(withDuration);
    }
    const q = channelId ? `?channelId=${encodeURIComponent(channelId)}` : '';
    const { items } = await request<{ items: Video[] }>(`/videos${q}`);
    return items.map(withDuration);
  },

  async getVideo(id: string): Promise<{
    video: Video;
    streamUrl: string;
    chapters: Chapter[];
    reactionCounts: Record<string, number>;
  }> {
    if (useMock) {
      const video = MOCK_VIDEOS.find((v) => v.videoId === id) ?? MOCK_VIDEOS[0];
      return {
        video: withDuration(video),
        streamUrl: '',
        chapters: video.videoId === 'kickoff' ? MOCK_CHAPTERS : [],
        reactionCounts: {},
      };
    }
    const res = await request<{
      video: Video;
      streamUrl: string;
      chapters: Chapter[];
      reactionCounts?: Record<string, number>;
    }>(`/videos/${id}`);
    return { ...res, video: withDuration(res.video), reactionCounts: res.reactionCounts ?? {} };
  },

  async listChannels(): Promise<Channel[]> {
    if (useMock) return MOCK_CHANNELS;
    const { items } = await request<{ items: Channel[] }>(`/channels`);
    return items;
  },

  async getChannel(id: string): Promise<Channel> {
    if (useMock) return MOCK_CHANNELS.find((c) => c.channelId === id) ?? MOCK_CHANNELS[0];
    return request<Channel>(`/channels/${id}`);
  },

  async me(): Promise<Me> {
    if (useMock) return MOCK_ME;
    return request<Me>(`/me`);
  },

  async myUploads(): Promise<Video[]> {
    if (useMock) return [];
    const { items } = await request<{ items: Video[] }>(`/me/uploads`);
    return items.map(withDuration);
  },

  async history(): Promise<Array<{ videoId: string; positionSec: number; viewedAt: string }>> {
    if (useMock)
      return [
        { videoId: 'kickoff', positionSec: 750, viewedAt: new Date().toISOString() },
        { videoId: 'product', positionSec: 400, viewedAt: new Date().toISOString() },
        { videoId: 'security', positionSec: 1200, viewedAt: new Date().toISOString() },
        { videoId: 'onboard', positionSec: 1120, viewedAt: new Date().toISOString() },
      ];
    const { items } = await request<{ items: Array<{ videoId: string; positionSec: number; viewedAt: string }> }>(
      `/me/history`,
    );
    return items;
  },

  async requestUpload(input: {
    title: string;
    description: string;
    channelId: string;
    contentType: string;
    filename: string;
    visibility: 'company' | 'link';
    tags: string[];
  }): Promise<{ videoId: string; uploadUrl: string }> {
    if (useMock) {
      return { videoId: 'mock', uploadUrl: '' };
    }
    return request(`/videos`, { method: 'POST', body: JSON.stringify(input) });
  },

  async publishVideo(id: string): Promise<void> {
    if (useMock) return;
    await request(`/videos/${id}/publish`, { method: 'POST' });
  },

  async updateVideo(
    id: string,
    patch: {
      title?: string;
      description?: string;
      channelId?: string;
      visibility?: 'company' | 'link';
      tags?: string[];
    },
  ): Promise<void> {
    if (useMock) return;
    await request(`/videos/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
  },

  async deleteVideo(id: string): Promise<void> {
    if (useMock) return;
    await request(`/videos/${id}`, { method: 'DELETE' });
  },

  async requestReplaceUpload(
    id: string,
    input: { contentType: string; filename: string },
  ): Promise<{ uploadUrl: string; uploadKey: string }> {
    if (useMock) return { uploadUrl: '', uploadKey: '' };
    return request(`/videos/${id}/replace`, { method: 'POST', body: JSON.stringify(input) });
  },

  async completeReplace(id: string, uploadKey: string): Promise<void> {
    if (useMock) return;
    await request(`/videos/${id}/replace/complete`, { method: 'POST', body: JSON.stringify({ uploadKey }) });
  },

  async requestThumbnailUpload(
    id: string,
    input: { contentType: string; filename: string },
  ): Promise<{ uploadUrl: string; uploadKey: string }> {
    if (useMock) return { uploadUrl: '', uploadKey: '' };
    return request(`/videos/${id}/thumbnail`, { method: 'POST', body: JSON.stringify(input) });
  },

  async completeThumbnailUpload(id: string, uploadKey: string): Promise<void> {
    if (useMock) return;
    await request(`/videos/${id}/thumbnail/complete`, { method: 'POST', body: JSON.stringify({ uploadKey }) });
  },

  async createChannel(input: { channelId: string; name: string; tint: string; description?: string }): Promise<void> {
    if (useMock) return;
    await request(`/channels`, { method: 'POST', body: JSON.stringify(input) });
  },

  async updateChannel(id: string, patch: { name?: string; tint?: string; description?: string }): Promise<void> {
    if (useMock) return;
    await request(`/channels/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
  },

  async deleteChannel(id: string, cascade?: boolean): Promise<void> {
    if (useMock) return;
    const q = cascade ? '?cascade=true' : '';
    await request(`/channels/${id}${q}`, { method: 'DELETE' });
  },

  async recordView(id: string): Promise<void> {
    if (useMock) return;
    await request(`/videos/${id}/view`, { method: 'POST', body: JSON.stringify({}) });
  },

  async saveProgress(
    id: string,
    positionSec: number,
    durationSec?: number,
    keepalive?: boolean,
  ): Promise<void> {
    if (useMock) return;
    await request(`/videos/${id}/progress`, {
      method: 'POST',
      body: JSON.stringify({ positionSec, durationSec }),
      // タブを閉じる直前でも投げ切るために fetch keepalive を使う。sendBeacon は
      // Authorization ヘッダを付けられないので Cognito Authorizer で 401 になり NG。
      keepalive,
    });
  },

  async getProgress(id: string): Promise<{ positionSec: number; durationSec: number | null; viewedAt: string | null }> {
    if (useMock) return { positionSec: 0, durationSec: null, viewedAt: null };
    return request(`/videos/${id}/progress`);
  },

  async toggleReaction(id: string, kind: 'like' | 'clap' | 'eye', on: boolean): Promise<void> {
    if (useMock) return;
    await request(`/videos/${id}/reaction`, { method: 'POST', body: JSON.stringify({ kind, on }) });
  },

  async toggleLater(id: string, on: boolean): Promise<void> {
    if (useMock) return;
    if (on) await request(`/me/later/${id}`, { method: 'POST' });
    else await request(`/me/later/${id}`, { method: 'DELETE' });
  },

  async listLaterVideoIds(): Promise<string[]> {
    if (useMock) return [];
    const { items } = await request<{ items: Array<{ videoId: string }> }>(`/me/later`);
    return items.map((r) => r.videoId).filter(Boolean);
  },

  async listMyReactions(id: string): Promise<Array<'like' | 'clap' | 'eye'>> {
    if (useMock) return [];
    const { items } = await request<{ items: Array<'like' | 'clap' | 'eye'> }>(`/videos/${id}/reactions/mine`);
    return items;
  },

  async adminStats(): Promise<{
    totalVideos: number;
    totalViews: number;
    topVideos: Array<{ videoId: string; title: string; views: number; tint: string }>;
  }> {
    if (useMock) {
      return {
        totalVideos: 72,
        totalViews: 1284,
        topVideos: MOCK_VIDEOS.slice(0, 5).map((v) => ({
          videoId: v.videoId,
          title: v.title,
          views: v.views,
          tint: v.tint,
        })),
      };
    }
    return request(`/admin/stats`);
  },

  async listAdmins(): Promise<Array<{
    userId: string;
    addedAt?: string;
    note?: string;
    email?: string;
    username?: string;
    displayName?: string;
  }>> {
    if (useMock) return [];
    const { items } = await request<{ items: Array<{ userId: string }> }>(`/admin/admins`);
    return items as Array<{
      userId: string;
      addedAt?: string;
      note?: string;
      email?: string;
      username?: string;
      displayName?: string;
    }>;
  },

  async addAdmin(hint: string): Promise<{ userId: string; email?: string; displayName?: string }> {
    if (useMock) return { userId: 'mock' };
    return request(`/admin/admins`, { method: 'POST', body: JSON.stringify({ hint }) });
  },

  async removeAdmin(userId: string): Promise<void> {
    if (useMock) return;
    await request(`/admin/admins/${userId}`, { method: 'DELETE' });
  },
};
